// Center Admin V2 — one page, role-aware.
//
//  role = org_admin / co_admin  → sees KPIs, Plan Usage, Co-admins, Instructors,
//                                  and the full Students roster (with search
//                                  + expand + adjust access).
//  role = instructor            → sees the same Students roster and can add /
//                                  manage students; Co-admins + Instructors
//                                  management sections are hidden.
//
// FrenchPrep-inspired layout kept in our indigo/slate palette. sonner toasts
// for feedback. Every action + info bit surfaces gracefully when email is not
// configured (the accept-invite / reset link is returned inline for hand share).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BadgeCheck, Building2, CalendarCheck2, Check, ChevronDown, ClipboardList, Copy,
  Crown, KeyRound, ListChecks, LineChart, Mail, Plus, RotateCcw, Search, Shield,
  Trash2, UserCog, UserMinus, UserPlus, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';

import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const EXAM_NAME = { celpip: 'CELPIP', pte: 'PTE' };

// ─── shared bits ────────────────────────────────────────────────────────────

function initials(name, email) {
  const s = (name || email || '?').trim();
  const parts = s.split(/\s+/);
  return (parts[0]?.[0] || '?').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysFromNow(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.round((d - Date.now()) / 86400000);
}

function CopyLine({ path }) {
  const [done, setDone] = useState(false);
  const url = (typeof window !== 'undefined' ? window.location.origin : '') + path;
  return (
    <span className="mt-1.5 flex items-center gap-2" data-testid="copy-line">
      <code className="truncate rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">{url}</code>
      <button
        type="button"
        onClick={async () => {
          try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* blocked */ }
        }}
        className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900"
      >
        {done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        {done ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

function ExamPill({ exam }) {
  return (
    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
      {EXAM_NAME[exam] || exam.toUpperCase()}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, testid, tone = 'slate' }) {
  const toneCls = {
    indigo: 'bg-indigo-50 text-indigo-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    sky: 'bg-sky-50 text-sky-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700',
    slate: 'bg-slate-100 text-slate-700',
  }[tone];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className={cn('inline-flex h-9 w-9 items-center justify-center rounded-lg', toneCls)}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <p className="mt-3 h-display text-3xl font-extrabold tabular-nums text-slate-900">{value}</p>
      <p className="mono-stat mt-1 !text-slate-500">{label}</p>
    </div>
  );
}

function ProgressBar({ used, cap, tone = 'indigo' }) {
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const barTone = tone === 'indigo' ? 'bg-indigo-500' : 'bg-emerald-500';
  return (
    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={cn('h-full rounded-full transition-all', barTone)} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── invite forms ───────────────────────────────────────────────────────────

function InviteForm({ kind, onSubmit, onCancel, exams }) {
  // kind = 'student' | 'instructor' | 'co-admin'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState(exams || []);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const canPickExams = kind === 'student' && (exams?.length || 0) > 1;

  const submit = async (ev) => {
    ev.preventDefault();
    if (!name.trim() || !email.trim()) return;
    if (canPickExams && picked.length === 0) return;
    setBusy(true); setResult(null);
    try {
      const body = { name: name.trim(), email: email.trim() };
      if (canPickExams) body.exams = picked;
      const r = await onSubmit(body);
      setResult({ ok: true, ...r });
      setName(''); setEmail(''); setPicked(exams || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not add.';
      setResult({ error: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const label = { student: 'Add student', instructor: 'Add instructor', 'co-admin': 'Add co-admin' }[kind];

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid={`invite-${kind}-form`}>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem]">
          <label className="mono-stat block">Full name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-9" data-testid={`invite-${kind}-name`} />
        </div>
        <div className="min-w-[16rem]">
          <label className="mono-stat block">Email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-9" data-testid={`invite-${kind}-email`} />
        </div>
        {canPickExams && (
          <div>
            <label className="mono-stat block">Exam access</label>
            <div className="mt-1 flex gap-1.5">
              {exams.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setPicked((p) => (p.includes(e) ? p.filter((x) => x !== e) : [...p, e]))}
                  className={cn(
                    'rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                    picked.includes(e)
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-300 text-slate-500 hover:text-slate-800',
                  )}
                  data-testid={`invite-${kind}-exam-${e}`}
                >
                  {picked.includes(e) && <Check className="mr-1 inline h-3 w-3" />}
                  {EXAM_NAME[e] || e.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
        <Button type="submit" size="sm" className="cta" disabled={busy} data-testid={`invite-${kind}-submit`}>
          <Plus className="h-3.5 w-3.5" /> {busy ? 'Adding…' : label}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          className="ml-1 text-sm text-slate-400 hover:text-slate-700"
        >
          Cancel
        </button>
      </form>
      {result?.error && <p className="mt-3 text-sm text-rose-600" data-testid={`invite-${kind}-error`}>{result.error}</p>}
      {result?.ok && (
        <div className="mt-3 border-l-2 border-emerald-500 pl-3 text-sm" data-testid={`invite-${kind}-result`}>
          {result.email_sent
            ? <span className="text-emerald-700">Invite emailed. They set a password from the link.</span>
            : (
              <span className="block text-slate-600">
                Added. Email isn’t set up yet — send them this link to set a password:
                <CopyLine path={result.accept_path} />
              </span>
            )}
        </div>
      )}
    </div>
  );
}

// ─── Set password inline card (WhatsApp-share flow) ──────────────────────────

function SetPasswordInline({ testId, onSubmit, onCancel }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault();
    if (pw.trim().length < 6) return;
    setBusy(true);
    try {
      await onSubmit(pw.trim());
      toast.success('Password set. Share it with the user.');
      onCancel?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set password.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2" data-testid={`${testId}-form`}>
      <div>
        <label className="mono-stat block">New password</label>
        <Input
          type="text"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="mt-1 h-9 w-56 font-mono"
          placeholder="Min 6 chars"
          autoFocus
          data-testid={`${testId}-input`}
        />
      </div>
      <Button type="submit" size="sm" className="cta" disabled={busy || pw.trim().length < 6} data-testid={`${testId}-submit`}>
        {busy ? 'Saving…' : 'Save password'}
      </Button>
      <button type="button" className="text-xs text-slate-400 hover:text-slate-700" onClick={onCancel}>Cancel</button>
    </form>
  );
}

// ─── Co-admins ──────────────────────────────────────────────────────────────

function CoAdminsSection({ owner, coAdmins, slots, me, onAdd, onRemove, onReset }) {
  const [showAdd, setShowAdd] = useState(false);
  const free = Math.max(0, (slots?.max || 0) - (slots?.used || 0));

  return (
    <section data-testid="co-admins-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="h-display text-xl font-bold text-slate-900">Co-admins</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {slots.used === 0 ? 'No co-admins yet' : `${slots.used} of ${slots.max} slots used`} · {free} slot{free === 1 ? '' : 's'} free
          </p>
        </div>
        {me.is_owner && free > 0 && (
          <Button
            size="sm"
            className="cta"
            onClick={() => setShowAdd((v) => !v)}
            data-testid="toggle-add-co-admin"
          >
            <UserPlus className="h-3.5 w-3.5" /> {showAdd ? 'Cancel' : 'Add co-admin'}
          </Button>
        )}
      </div>

      {showAdd && me.is_owner && (
        <InviteForm kind="co-admin" onSubmit={onAdd} onCancel={() => setShowAdd(false)} />
      )}

      {owner && (
        <div
          className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50/30 p-4"
          data-testid="owner-card"
          style={{ borderLeftWidth: 4, borderLeftColor: 'rgb(99 102 241)' }}
        >
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-indigo-100 font-semibold text-indigo-700">
            {initials(owner.name, owner.email)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate font-display text-[15px] font-semibold text-slate-900">{owner.name}</p>
              {owner.user_id === me.user_id && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">you</span>}
              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                <Crown className="h-3 w-3" /> Owner
              </span>
            </div>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
              <Mail className="h-3 w-3" /> {owner.email}
            </p>
          </div>
        </div>
      )}

      {coAdmins.length === 0 ? (
        <div className="mt-3 flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white/50 px-4 py-10 text-center" data-testid="co-admins-empty">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-100">
            <UserCog className="h-4 w-4 text-slate-500" />
          </div>
          <p className="mt-3 text-sm font-medium text-slate-700">No co-admins yet</p>
          <p className="mt-1 text-xs text-slate-500">Add up to {slots.max} co-admins to help run the center.</p>
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {coAdmins.map((c) => (
            <li
              key={c.user_id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4"
              data-testid={`co-admin-row-${c.user_id}`}
            >
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-slate-100 font-semibold text-slate-700">
                {initials(c.name, c.email)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-display text-[15px] font-semibold text-slate-900">{c.name}</p>
                  {c.user_id === me.user_id && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">you</span>}
                  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-800">Co-admin</span>
                  {c.pending && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">invite pending</span>}
                </div>
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                  <Mail className="h-3 w-3" /> {c.email} · joined {fmtDate(c.created_at)}
                </p>
              </div>
              {me.is_owner && c.user_id !== me.user_id && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-indigo-400 hover:text-indigo-700"
                    onClick={() => onReset(c)}
                    data-testid={`co-admin-reset-${c.user_id}`}
                  >
                    <KeyRound className="h-3.5 w-3.5" /> Reset
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-rose-300 hover:text-rose-700"
                    onClick={() => onRemove(c)}
                    data-testid={`co-admin-remove-${c.user_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Instructors ────────────────────────────────────────────────────────────

function InstructorsSection({ instructors, me, orgExams, onAdd, onRemove, onReset, onSetPassword }) {
  const [showAdd, setShowAdd] = useState(false);
  const [pwFor, setPwFor] = useState(null); // user_id currently entering pw

  return (
    <section data-testid="instructors-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="h-display text-xl font-bold text-slate-900">Instructors</h2>
          <p className="mt-0.5 text-sm text-slate-500">{instructors.length} instructor{instructors.length === 1 ? '' : 's'} in your center</p>
        </div>
        <Button size="sm" className="cta" onClick={() => setShowAdd((v) => !v)} data-testid="toggle-add-instructor">
          <UserPlus className="h-3.5 w-3.5" /> {showAdd ? 'Cancel' : 'Add instructor'}
        </Button>
      </div>

      {showAdd && (
        <InviteForm kind="instructor" onSubmit={onAdd} onCancel={() => setShowAdd(false)} exams={orgExams} />
      )}

      {instructors.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed border-slate-200 bg-white/50 p-6 text-center text-sm text-slate-500" data-testid="instructors-empty">
          No instructors yet. Add one above — they get their own login and can add and manage students.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {instructors.map((t) => (
            <li
              key={t.user_id}
              className="rounded-lg border border-slate-200 bg-white p-4"
              data-testid={`instructor-row-${t.user_id}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-slate-100 font-semibold text-slate-700">
                  {initials(t.name, t.email)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-display text-[15px] font-semibold text-slate-900">{t.name}</p>
                    {t.user_id === me.user_id && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">you</span>}
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-800">Instructor</span>
                    {t.pending && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">invite pending</span>}
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <Mail className="h-3 w-3" /> {t.email}
                    <span className="hidden sm:inline">·</span>
                    <span>Joined {fmtDate(t.created_at)}</span>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-right text-xs">
                    <span className="block h-display text-lg font-bold tabular-nums text-slate-900" data-testid={`instructor-students-${t.user_id}`}>{t.students_added ?? 0}</span>
                    <span className="mono-stat !text-slate-500">students</span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    {(t.exams || orgExams || []).map((e) => <ExamPill key={e} exam={e} />)}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-indigo-400 hover:text-indigo-700"
                      onClick={() => onReset(t)}
                      data-testid={`instructor-reset-${t.user_id}`}
                    >
                      <KeyRound className="h-3.5 w-3.5" /> Reset
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-500"
                      onClick={() => setPwFor(pwFor === t.user_id ? null : t.user_id)}
                      data-testid={`instructor-setpw-${t.user_id}`}
                    >
                      <BadgeCheck className="h-3.5 w-3.5" /> Set password
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-rose-300 hover:text-rose-700"
                      onClick={() => onRemove(t)}
                      data-testid={`instructor-remove-${t.user_id}`}
                    >
                      <UserMinus className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
              {pwFor === t.user_id && (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/50 p-3">
                  <SetPasswordInline
                    testId={`instructor-setpw-${t.user_id}`}
                    onSubmit={(pw) => onSetPassword(t, pw)}
                    onCancel={() => setPwFor(null)}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Students ───────────────────────────────────────────────────────────────

function AccessBadge({ expiresAt }) {
  if (!expiresAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
        <BadgeCheck className="h-3 w-3" /> Unlimited
      </span>
    );
  }
  const d = daysFromNow(expiresAt);
  if (d === null) return null;
  const tone = d <= 0 ? 'rose' : d <= 7 ? 'amber' : 'slate';
  const cls = {
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
  }[tone];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold', cls)}>
      <CalendarCheck2 className="h-3 w-3" />
      {d <= 0 ? 'expired' : `${d}d left`}
    </span>
  );
}

function AdjustAccessPopover({ onAdjust, onClose }) {
  const [date, setDate] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const clickOff = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    document.addEventListener('mousedown', clickOff);
    return () => document.removeEventListener('mousedown', clickOff);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute right-0 top-10 z-20 w-72 rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
      data-testid="adjust-access-popover"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Adjust access</p>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className="mono-stat mt-3 !text-slate-500">Extend</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {[7, 14, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onAdjust({ delta_days: d })}
            className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            data-testid={`access-extend-${d}`}
          >
            +{d} d
          </button>
        ))}
      </div>
      <p className="mono-stat mt-3 !text-slate-500">Shorten</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {[7, 30].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onAdjust({ delta_days: -d })}
            className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
            data-testid={`access-shorten-${d}`}
          >
            −{d} d
          </button>
        ))}
      </div>
      <p className="mono-stat mt-3 !text-slate-500">Set until</p>
      <div className="mt-1.5 flex items-center gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-8 flex-1 rounded-md border border-slate-300 px-2 text-sm"
          data-testid="access-set-until"
        />
        <Button
          size="sm"
          className="cta h-8 px-3"
          onClick={() => date && onAdjust({ set_until: date })}
          data-testid="access-set-until-submit"
        >
          Set
        </Button>
      </div>
      <button
        type="button"
        onClick={() => onAdjust({ unlimited: true })}
        className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
        data-testid="access-unlimited"
      >
        <BadgeCheck className="h-3.5 w-3.5" /> Set to unlimited
      </button>
    </div>
  );
}

function StudentCard({ s, me, canManageAllStudents, onExpand, expanded, onReset, onSetPassword, onRemove, onAdjust }) {
  const [showAccess, setShowAccess] = useState(false);
  const [showPw, setShowPw] = useState(false);
  return (
    <li
      className={cn(
        'rounded-xl border bg-white transition-colors',
        expanded ? 'border-indigo-300 shadow-sm' : 'border-slate-200 hover:border-slate-300',
      )}
      data-testid={`student-row-${s.user_id}`}
    >
      <button
        type="button"
        onClick={onExpand}
        className="flex w-full flex-wrap items-center gap-3 px-4 py-3.5 text-left"
        data-testid={`student-toggle-${s.user_id}`}
      >
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
          {initials(s.name, s.email)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-display text-[15px] font-semibold text-slate-900">{s.name}</p>
            {(s.exams || []).map((e) => <ExamPill key={e} exam={e} />)}
            {s.pending && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">invite pending</span>}
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {s.email}
            {canManageAllStudents && s.added_by_name ? <> · Added by {s.added_by_name}</> : null}
            {s.created_at ? <> · Added {fmtDate(s.created_at)}</> : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <AccessBadge expiresAt={s.expires_at} />
          <div className="text-right">
            <p className="text-sm font-semibold tabular-nums text-slate-900" data-testid={`student-items-${s.user_id}`}>
              {s.items_done ?? 0} items completed
            </p>
            <p className="text-xs text-slate-400">
              {s.last_active_at ? `Last active ${fmtDate(s.last_active_at)}` : 'Not started yet'}
            </p>
          </div>
          <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', expanded && 'rotate-180')} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 text-center">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid={`student-stat-items-done-${s.user_id}`}>{s.items_done ?? 0}</p>
              <p className="mono-stat !text-slate-500">Items Done</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 text-center">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid={`student-stat-attempted-${s.user_id}`}>{s.items_attempted ?? 0}</p>
              <p className="mono-stat !text-slate-500">Total Attempted</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 text-center">
              <p className="h-display text-sm font-bold tabular-nums text-slate-900">{s.last_active_at ? fmtDate(s.last_active_at) : '—'}</p>
              <p className="mono-stat !text-slate-500">Last Active</p>
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-indigo-400 hover:text-indigo-700"
              onClick={() => onReset(s)}
              data-testid={`student-reset-${s.user_id}`}
            >
              <KeyRound className="h-4 w-4" /> Reset password
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-500"
              onClick={() => setShowPw((v) => !v)}
              data-testid={`student-setpw-${s.user_id}`}
            >
              <BadgeCheck className="h-4 w-4" /> Set password
            </button>
            <Link
              to={`/org/students/${s.user_id}/library`}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
              data-testid={`student-library-${s.user_id}`}
            >
              <ListChecks className="h-4 w-4" /> View & Assign Items
            </Link>
            <Link
              to={`/org/students/${s.user_id}/progress`}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-emerald-400 hover:text-emerald-700"
              data-testid={`student-progress-${s.user_id}`}
            >
              <LineChart className="h-4 w-4" /> View Progress
            </Link>
            <div className="relative">
              <button
                type="button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-500"
                onClick={() => setShowAccess((v) => !v)}
                data-testid={`student-adjust-access-${s.user_id}`}
              >
                <ClipboardList className="h-4 w-4" /> Adjust access
              </button>
              {showAccess && (
                <AdjustAccessPopover
                  onAdjust={async (body) => {
                    try {
                      await onAdjust(s, body);
                      toast.success('Access updated.');
                      setShowAccess(false);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Could not update access.');
                    }
                  }}
                  onClose={() => setShowAccess(false)}
                />
              )}
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
              onClick={() => onRemove(s)}
              data-testid={`student-remove-${s.user_id}`}
            >
              <UserMinus className="h-4 w-4" /> Remove from roster
            </button>
          </div>

          {showPw && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 p-3">
              <SetPasswordInline
                testId={`student-setpw-${s.user_id}`}
                onSubmit={(pw) => onSetPassword(s, pw)}
                onCancel={() => setShowPw(false)}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function StudentsSection({
  students, me, orgExams, activeAssignments, onAdd, onRemove, onReset, onSetPassword, onAdjust,
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | mine
  const [expandedId, setExpandedId] = useState(null);

  const canManageAllStudents = ['org_admin', 'co_admin', 'platform_admin'].includes(me.role);

  const filtered = useMemo(() => {
    let list = students;
    if (filter === 'mine') list = list.filter((s) => s.added_by === me.user_id);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((s) => (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
    return list;
  }, [students, query, filter, me.user_id]);

  const activeToday = useMemo(() => {
    const cutoff = Date.now() - 86400000;
    return students.filter((s) => s.last_active_at && new Date(s.last_active_at).getTime() >= cutoff).length;
  }, [students]);

  return (
    <section data-testid="students-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="h-display text-xl font-bold text-slate-900">Students</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {students.length} student{students.length === 1 ? '' : 's'} in your center · {activeToday} active today · {activeAssignments} active assignment{activeAssignments === 1 ? '' : 's'}
          </p>
        </div>
        <Button size="sm" className="cta" onClick={() => setShowAdd((v) => !v)} data-testid="toggle-add-student">
          <UserPlus className="h-3.5 w-3.5" /> {showAdd ? 'Cancel' : 'Add student'}
        </Button>
      </div>

      {showAdd && (
        <InviteForm kind="student" onSubmit={onAdd} onCancel={() => setShowAdd(false)} exams={orgExams} />
      )}

      {students.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search students by name or email…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
              data-testid="students-search"
            />
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {[{ id: 'all', label: 'All students' }, { id: 'mine', label: 'Added by me' }].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  filter === f.id ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800',
                )}
                data-testid={`students-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-slate-200 bg-white/50 p-8 text-center text-sm text-slate-500" data-testid="students-empty">
          {students.length === 0
            ? 'No students yet. Add one above to give them access.'
            : 'No students match this search or filter.'}
        </p>
      ) : (
        <ul className="mt-4 space-y-2">
          {filtered.map((s) => (
            <StudentCard
              key={s.user_id}
              s={s}
              me={me}
              canManageAllStudents={canManageAllStudents}
              expanded={expandedId === s.user_id}
              onExpand={() => setExpandedId(expandedId === s.user_id ? null : s.user_id)}
              onReset={onReset}
              onSetPassword={onSetPassword}
              onRemove={onRemove}
              onAdjust={onAdjust}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Branding: "Provisioned by [firm]" editor ────────────────────────────────

function BrandingSection({ initial, onSave }) {
  const [text, setText] = useState(initial?.provisioned_by || '');
  const [enabled, setEnabled] = useState(!!initial?.enabled);
  const [busy, setBusy] = useState(false);
  const dirty =
    (text || '').trim() !== (initial?.provisioned_by || '') ||
    enabled !== !!initial?.enabled;

  const submit = async () => {
    setBusy(true);
    try {
      await onSave({ provisioned_by: text.trim(), enabled });
      toast.success(
        enabled && text.trim()
          ? 'Provisioned-by badge is live for your staff and students.'
          : 'Provisioned-by badge disabled.',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save branding.');
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="branding-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="mono-stat !text-slate-500">Custom branding</p>
          <h2 className="mt-1 h-display text-lg font-bold text-slate-900">Provisioned by badge</h2>
          <p className="mt-1 max-w-xl text-sm text-slate-500">
            Shown in the sidebar of every student, instructor, and co-admin in your center. Use your firm’s name exactly as you’d like it to appear.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((v) => !v)}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
            enabled ? 'bg-indigo-500' : 'bg-slate-300',
          )}
          data-testid="branding-toggle"
        >
          <span className={cn(
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-5' : 'translate-x-0.5',
          )} />
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <div>
          <label className="mono-stat block">Firm name (max 60 chars)</label>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 60))}
            placeholder="e.g. Pathway IRCC Services"
            className="mt-1 h-10 max-w-md"
            disabled={!enabled}
            data-testid="branding-name-input"
          />
        </div>
        <Button
          className="cta h-10"
          onClick={submit}
          disabled={busy || !dirty || (enabled && !text.trim())}
          data-testid="branding-save"
        >
          {busy ? 'Saving…' : 'Save branding'}
        </Button>
      </div>

      <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-3">
        <p className="mono-stat !text-slate-500">Preview (as staff and students see it)</p>
        <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-white">
          <span className="mono-stat !text-slate-400">Provisioned by</span>
          <span className="font-semibold" data-testid="branding-preview">
            {enabled && text.trim() ? text.trim() : '—'}
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── page ───────────────────────────────────────────────────────────────────

export default function OrgPortal() {
  const { user, effectiveRole } = useApp();
  const isAdmin = ['org_admin', 'co_admin', 'platform_admin'].includes(effectiveRole);
  const [sp, setSp] = useSearchParams();
  const welcome = sp.get('welcome') === '1';

  const [center, setCenter] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignmentCount, setAssignmentCount] = useState(0);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const calls = [api.orgStudents(false)];
      if (isAdmin) {
        calls.push(api.orgCenter());
        calls.push(api.orgInstructors());
      } else {
        calls.push(api.orgContext());
      }
      const res = await Promise.all(calls);
      setStudents(res[0].data.students || []);
      if (isAdmin) {
        setCenter(res[1].data);
        setInstructors(res[2].data.instructors || []);
      } else {
        const ctx = res[1].data;
        // Instructors see a lightweight center payload derived from context
        setCenter({
          org: { name: ctx.org.name, exams: ctx.org.exams, seats: ctx.org.seats },
          me: { user_id: user?.user_id, name: user?.name, email: user?.email, role: 'instructor', is_owner: false },
          owner: null,
          stats: {
            instructors: ctx.instructor_count,
            active_students: ctx.student_count,
            all_time_students: ctx.student_count,
            new_this_month: 0,
          },
          plan: { students: { used: ctx.student_count, cap: ctx.org.seats || 0 }, instructors: { used: ctx.instructor_count, cap: 0 } },
          co_admins: [],
          co_admin_slots: { used: 0, max: 0 },
          branding: null,
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load center data.');
    }
  }, [isAdmin, user]);

  useEffect(() => { load(); }, [load]);

  // Roll active-assignment count from student rows (cheap; keeps 1 fewer API).
  useEffect(() => {
    // Count is derived from students who have any assigned items open on the roster
    // For now approximate via "pending" — real counter needs a dedicated endpoint.
    setAssignmentCount(students.filter((s) => (s.items_attempted || 0) > (s.items_done || 0)).length);
  }, [students]);

  const doAction = async (fn, okMsg) => {
    try { const r = await fn(); if (okMsg) toast.success(okMsg); await load(); return r?.data; }
    catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong.'); throw err; }
  };

  const addStudent = async (body) => { const r = await api.orgAddStudent(body); await load(); return r.data; };
  const removeStudent = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the roster?`)) return;
    await doAction(() => api.orgDeactivateStudent(s.user_id), 'Student removed.');
  };
  const resetStudent = async (s) => {
    const r = await doAction(() => api.orgResetLink(s.user_id));
    if (r?.email_sent) toast.success(`Reset link emailed to ${s.name}.`);
    else if (r?.accept_path) toast.info(`Email off. Share this link:\n${window.location.origin}${r.accept_path}`);
  };
  const setStudentPassword = async (s, pw) => { await api.orgStudentSetPw(s.user_id, pw); await load(); };
  const adjustStudentAccess = async (s, body) => { await api.orgStudentAccess(s.user_id, body); await load(); };

  const addInstructor = async (body) => { const r = await api.orgAddInstructor(body); await load(); return r.data; };
  const removeInstructor = async (t) => {
    if (!window.confirm(`Remove ${t.name} from the center? Their students stay with the center.`)) return;
    await doAction(() => api.orgRemoveInstructor(t.user_id), 'Instructor removed.');
  };
  const resetInstructor = async (t) => {
    const r = await doAction(() => api.orgInstructorReset(t.user_id));
    if (r?.email_sent) toast.success(`Reset link emailed to ${t.name}.`);
    else if (r?.accept_path) toast.info(`Email off. Share this link:\n${window.location.origin}${r.accept_path}`);
  };
  const setInstructorPassword = async (t, pw) => { await api.orgInstructorSetPw(t.user_id, pw); await load(); };

  const addCoAdmin = async (body) => { const r = await api.orgAddCoAdmin(body); await load(); return r.data; };
  const removeCoAdmin = async (c) => {
    if (!window.confirm(`Remove ${c.name} as a co-admin? They keep no residual access.`)) return;
    await doAction(() => api.orgRemoveCoAdmin(c.user_id), 'Co-admin removed.');
  };
  const resetCoAdmin = async (c) => {
    const r = await doAction(() => api.orgCoAdminReset(c.user_id));
    if (r?.email_sent) toast.success(`Reset link emailed to ${c.name}.`);
    else if (r?.accept_path) toast.info(`Email off. Share this link:\n${window.location.origin}${r.accept_path}`);
  };
  const saveBranding = async (body) => { await api.orgSetBranding(body); await load(); };

  if (error) return <AppShell><p className="text-rose-600" data-testid="org-error">{error}</p></AppShell>;
  if (!center) return <AppShell><p className="text-slate-400">Loading center…</p></AppShell>;

  const meDisplay = center.me?.name || user?.name || '';
  const roleLabel = ({
    org_admin: 'Center Administration',
    co_admin: 'Center Administration · Co-admin',
    instructor: 'Teacher Admin',
    platform_admin: 'Platform Admin (Center View)',
  }[effectiveRole]) || 'Center';

  const activeStudents = center.stats.active_students;
  const activeToday = students.filter((s) => s.last_active_at && new Date(s.last_active_at).getTime() >= (Date.now() - 86400000)).length;

  return (
    <AppShell>
      <div className="space-y-10 pb-16" data-testid="org-page">
        {/* ── Header ── */}
        <header className="flex flex-wrap items-start gap-4">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-indigo-100 text-indigo-700">
            <Shield className="h-7 w-7" />
          </div>
          <div className="min-w-0">
            <h1 className="h-display text-3xl font-extrabold text-slate-900 sm:text-4xl" data-testid="center-name">
              {center.org.name}
            </h1>
            <p className="mt-1 text-sm text-slate-500" data-testid="center-subtitle">
              {roleLabel}{meDisplay ? <> · {meDisplay}</> : null}
            </p>
          </div>
        </header>

        {welcome && (
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-indigo-200 bg-indigo-50/50 px-5 py-4" data-testid="org-welcome">
            <div>
              <p className="font-display text-[15px] font-semibold text-slate-900">You’re all set, {user?.name?.split(' ')[0] || 'welcome'}.</p>
              <p className="mt-1 text-sm text-slate-600">Add your students and instructors below to give them access.</p>
            </div>
            <button type="button" onClick={() => setSp({}, { replace: true })} className="shrink-0 text-sm text-slate-400 hover:text-slate-700">Dismiss</button>
          </div>
        )}

        {/* ── KPI cards ── */}
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="org-kpis">
          {isAdmin ? (
            <>
              <KpiCard icon={UserCog} tone="sky" label="INSTRUCTORS" value={center.stats.instructors} testid="kpi-instructors" />
              <KpiCard icon={Users} tone="emerald" label="ACTIVE STUDENTS" value={activeStudents} testid="kpi-active-students" />
              <KpiCard icon={LineChart} tone="indigo" label="NEW THIS MONTH" value={center.stats.new_this_month} testid="kpi-new-this-month" />
              <KpiCard icon={BadgeCheck} tone="amber" label="ALL-TIME" value={center.stats.all_time_students} testid="kpi-all-time" />
            </>
          ) : (
            <>
              <KpiCard icon={Users} tone="emerald" label="STUDENTS" value={activeStudents} testid="kpi-students" />
              <KpiCard icon={ClipboardList} tone="indigo" label="ACTIVE ASSIGNMENTS" value={assignmentCount} testid="kpi-active-assignments" />
              <KpiCard icon={CalendarCheck2} tone="sky" label="ACTIVE TODAY" value={activeToday} testid="kpi-active-today" />
              <KpiCard icon={LineChart} tone="amber" label="THIS MONTH" value={center.stats.new_this_month} testid="kpi-this-month" />
            </>
          )}
        </section>

        {/* ── Plan Usage (admin only) ── */}
        {isAdmin && (
          <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="plan-usage">
            <p className="mono-stat !text-slate-500">Plan Usage</p>
            <div className="mt-3 grid gap-6 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-800">Students</span>
                  <span className="tabular-nums text-slate-500">
                    <span className="font-semibold text-slate-900" data-testid="plan-students-used">{center.plan.students.used}</span>
                    /<span data-testid="plan-students-cap">{center.plan.students.cap || '∞'}</span>
                  </span>
                </div>
                <ProgressBar used={center.plan.students.used} cap={center.plan.students.cap} tone="indigo" />
              </div>
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-800">Instructors</span>
                  <span className="tabular-nums text-slate-500">
                    <span className="font-semibold text-slate-900" data-testid="plan-instructors-used">{center.plan.instructors.used}</span>
                    /<span data-testid="plan-instructors-cap">{center.plan.instructors.cap || '∞'}</span>
                  </span>
                </div>
                <ProgressBar used={center.plan.instructors.used} cap={center.plan.instructors.cap} tone="emerald" />
              </div>
            </div>
          </section>
        )}

        {/* ── Custom branding (feature-gated by Platform Admin) ── */}
        {isAdmin && center.features?.provisioned_by_badge && (
          <BrandingSection initial={center.branding} onSave={saveBranding} />
        )}

        {/* ── Co-admins (admin only) ── */}
        {isAdmin && center.owner && (
          <CoAdminsSection
            owner={center.owner}
            coAdmins={center.co_admins || []}
            slots={center.co_admin_slots || { used: 0, max: 0 }}
            me={center.me}
            onAdd={addCoAdmin}
            onRemove={removeCoAdmin}
            onReset={resetCoAdmin}
          />
        )}

        {/* ── Instructors (admin only) ── */}
        {isAdmin && (
          <InstructorsSection
            instructors={instructors}
            me={center.me}
            orgExams={center.org.exams || []}
            onAdd={addInstructor}
            onRemove={removeInstructor}
            onReset={resetInstructor}
            onSetPassword={setInstructorPassword}
          />
        )}

        {/* ── Students (everyone) ── */}
        <StudentsSection
          students={students}
          me={center.me}
          orgExams={center.org.exams || ['celpip', 'pte']}
          activeAssignments={assignmentCount}
          onAdd={addStudent}
          onRemove={removeStudent}
          onReset={resetStudent}
          onSetPassword={setStudentPassword}
          onAdjust={adjustStudentAccess}
        />
      </div>
    </AppShell>
  );
}
